<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { PageDetailRow } from '@/db/content';
import { useUiDisplayLocale } from '@/composables/useUiDisplayLocale';
import { useContentStore } from '@/stores/content';
import SidebarNav from '@/components/navigation/SidebarNav.vue';
import type { ApiImage } from '@/types/images';
import { useAuthStore } from '@/stores/auth';
import CallToActionBlock from '@/components/blocks/CallToActionBlock.vue';
import CheckListBlock from '@/components/blocks/CheckListBlock.vue';
import FormBlock from '@/components/blocks/FormBlock.vue';
import CentralHeadingBlock from '@/components/blocks/CentralHeadingBlock.vue';
import PageLinkCardBlock from '@/components/blocks/PageLinkCardBlock.vue';
import ThreePageLinkBlock from '@/components/blocks/ThreePageLinkBlock.vue';
import ProcessBlock from '@/components/blocks/ProcessBlock.vue';
import PeopleBlock from '@/components/blocks/PeopleBlock.vue';
import PersonBlock from '@/components/blocks/PersonBlock.vue';
import ProcessOverviewBlock from '@/components/blocks/ProcessOverviewBlock.vue';
import ProtectedBlockMessage from './blocks/ProtectedBlockMessage.vue';
import QuoteBlock from '@/components/blocks/QuoteBlock.vue';
import PullQuoteBlock from '@/components/blocks/PullQuoteBlock.vue';
import RichTextBlock from '@/components/blocks/RichTextBlock.vue';
import FeatureCardBlock from '@/components/blocks/FeatureCardBlock.vue';
import StatCardsBlock from '@/components/blocks/StatCardsBlock.vue';
import TickFeatureCardsBlock from '@/components/blocks/TickFeatureCardsBlock.vue';
import PhotoCollageBlock from '@/components/blocks/PhotoCollageBlock.vue';
import NewsArticleBlock from '@/components/blocks/NewsArticleBlock.vue';
import NewsSummaryBlock from '@/components/blocks/NewsSummaryBlock.vue';
import TrainingCentreListBlock from '@/components/blocks/TrainingCentreListBlock.vue';
import ProgramListBlock from '@/components/blocks/ProgramListBlock.vue';
import AuthorisationRequiredMessageBlock from '@/components/blocks/AuthorisationRequiredMessageBlock.vue';
import ImageAndTextCardBlock from '@/components/blocks/ImageAndTextCardBlock.vue';
import {
  authSnapshotFromSession,
  resolveContentBlocksForAuth,
  type ContentBlockItem,
} from '@/components/contentBlocks';

type PageImage = ApiImage;

type PageDetail = PageDetailRow & {
  title?: string;
  image?: PageImage | null;
  content?: ContentBlockItem[];
  filter_card_title?: string | null;
};

const props = defineProps<{
  page: PageDetail;
  listKind: 'programs' | 'training-centres';
}>();

const blockComponents: Record<string, import('vue').Component> = {
  central_heading: CentralHeadingBlock,
  rich_text: RichTextBlock,
  quote: QuoteBlock,
  pull_quote: PullQuoteBlock,
  image_and_text_card: ImageAndTextCardBlock,
  call_to_action: CallToActionBlock,
  check_list: CheckListBlock,
  page_link_card: PageLinkCardBlock,
  three_page_link: ThreePageLinkBlock,
  process_block: ProcessBlock,
  process_overview: ProcessOverviewBlock,
  people: PeopleBlock,
  person: PersonBlock,
  feature_card: FeatureCardBlock,
  stat_cards: StatCardsBlock,
  tick_feature_cards: TickFeatureCardsBlock,
  photo_collage: PhotoCollageBlock,
  news_article: NewsArticleBlock,
  news_summary: NewsSummaryBlock,
  authorisation_required_message: AuthorisationRequiredMessageBlock,
  form: FormBlock,
  protected_message: ProtectedBlockMessage,
};

type BlockLayoutConfig = {
  tabletSpan?: 1 | 2 | 3 | 4;
  desktopSpan?: 1 | 2 | 3 | 4 | 5 | 6;
};

const DEFAULT_BLOCK_LAYOUT: Required<BlockLayoutConfig> = {
  tabletSpan: 4,
  desktopSpan: 6,
};

const BLOCK_LAYOUT_REGISTRY: Record<string, BlockLayoutConfig> = {
  check_list: {
    tabletSpan: 2,
    desktopSpan: 3,
  },
  feature_card: {
    tabletSpan: 2,
    desktopSpan: 3,
  },
  process_block: {
    tabletSpan: 2,
    desktopSpan: 3,
  },
  page_link_card: {
    tabletSpan: 2,
    desktopSpan: 2,
  },
};

const { t } = useI18n();
const authStore = useAuthStore();
const contentStore = useContentStore();

const currentLocale = useUiDisplayLocale();

const isNormalPageContentStyle = computed(() => {
  const t = (props.page?.meta as { type?: string } | undefined)?.type;
  return t === 'home.NormalPage' && props.page?.header_style === 'content';
});

const contentSidebarModel = computed(() => {
  if (!isNormalPageContentStyle.value) return null;
  const id = props.page?.id;
  if (typeof id !== 'number') return null;
  return contentStore.getNormalPageContentSidebar(currentLocale.value, id);
});

const showContentSidebar = computed(
  () => contentSidebarModel.value != null && contentSidebarModel.value.items.length > 0
);

const contentBlocks = computed(() => {
  const content = props.page?.content;
  if (!Array.isArray(content)) return [];
  return resolveContentBlocksForAuth(
    content as ContentBlockItem[],
    authSnapshotFromSession(authStore.isReady, authStore.isAuthenticated, authStore.currentUser),
    { wagtailContentPreview: contentStore.isCurrentPreviewMode }
  );
});

const filterCardTitle = computed(() => {
  const raw = props.page?.filter_card_title;
  const custom = typeof raw === 'string' ? raw.trim() : '';
  if (custom) return custom;
  return props.listKind === 'programs'
    ? t('programList.filterHeading')
    : t('trainingCentreList.filterHeading');
});

function blockComponent(type: string) {
  return blockComponents[type] ?? null;
}

function blockLayoutClasses(type: string) {
  const cfg = BLOCK_LAYOUT_REGISTRY[type] ?? {};
  const tablet = cfg.tabletSpan ?? DEFAULT_BLOCK_LAYOUT.tabletSpan;
  const desktop = cfg.desktopSpan ?? DEFAULT_BLOCK_LAYOUT.desktopSpan;
  return [
    `home-page__block--md-${tablet}`,
    `home-page__block--lg-${desktop}`,
  ];
}

function blockProps(block: ContentBlockItem): Record<string, unknown> {
  const v = block.value;
  if (typeof v === 'string') {
    return { html: v };
  }
  const base = (v ?? {}) as Record<string, unknown>;
  const pageId = props.page?.id;
  if (block.type === 'process_block' || block.type === 'need_help') {
    const out: Record<string, unknown> = {
      ...base,
      ...(block.type === 'process_block'
        ? { wagtailContentPreview: contentStore.isCurrentPreviewMode }
        : {}),
    };
    if (
      typeof pageId === 'number' &&
      typeof block.id === 'string' &&
      block.id.trim()
    ) {
      out.pageId = pageId;
      out.streamBlockId = block.id;
    }
    return out;
  }
  return base;
}

const directoryBlockLayoutClasses = [
  'home-page__block--md-4',
  'home-page__block--lg-6',
];
</script>

<template>
  <div
    v-if="showContentSidebar && contentSidebarModel"
    class="home-page__with-sidebar home-page__with-sidebar--section"
  >
    <aside class="home-page__sidebar">
      <SidebarNav
        :title="contentSidebarModel.title"
        :items="contentSidebarModel.items"
      />
    </aside>
    <div
      class="home-page__main page-content page-content-grid"
      role="main"
    >
      <div
        v-for="(block, index) in contentBlocks"
        :key="block.id ?? index"
        class="home-page__block"
        :class="[blockLayoutClasses(block.type), `home-page__block--${block.type}`]"
      >
        <component
          :is="blockComponent(block.type)"
          v-bind="blockProps(block)"
        />
      </div>
      <div
        class="home-page__block"
        :class="[...directoryBlockLayoutClasses, 'home-page__block--directory']"
      >
        <ProgramListBlock
          v-if="listKind === 'programs'"
          :filter-card-title="filterCardTitle"
        />
        <TrainingCentreListBlock
          v-else
          :filter-card-title="filterCardTitle"
        />
      </div>
    </div>
  </div>

  <div
    v-else
    class="page-content page-content-grid"
    :class="{ 'home-page__main-below-content-header': isNormalPageContentStyle }"
    role="main"
  >
    <div
      v-for="(block, index) in contentBlocks"
      :key="block.id ?? index"
      class="home-page__block"
      :class="[blockLayoutClasses(block.type), `home-page__block--${block.type}`]"
    >
      <component
        :is="blockComponent(block.type)"
        v-bind="blockProps(block)"
      />
    </div>
    <div
      class="home-page__block"
      :class="[...directoryBlockLayoutClasses, 'home-page__block--directory']"
    >
      <ProgramListBlock
        v-if="listKind === 'programs'"
        :filter-card-title="filterCardTitle"
      />
      <TrainingCentreListBlock
        v-else
        :filter-card-title="filterCardTitle"
      />
    </div>
  </div>
</template>

<style scoped>
.home-page__with-sidebar {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.home-page__with-sidebar--section {
  margin-top: 0;
  padding-top: 24px;
  padding-bottom: 24px;
}

.home-page__main-below-content-header {
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0;
}
@media (min-width: 640px) {
  .home-page__main-below-content-header {
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
  }
}
@media (min-width: 1024px) {
  .home-page__main-below-content-header {
    padding-top: 3rem;
    padding-bottom: 3rem;
  }

  .home-page__with-sidebar {
    flex-direction: row;
    align-items: stretch;
    gap: 2rem;
  }

  .home-page__sidebar {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    width: 16rem;
  }

  .home-page__main {
    flex: 1;
    min-width: 0;
  }
}

.page-content {
  margin-top: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

.page-content-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.home-page__block {
  min-width: 0;
}

@media (min-width: 768px) {
  .page-content-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .home-page__block--md-1 {
    grid-column: span 1;
  }

  .home-page__block--md-2 {
    grid-column: span 2;
  }

  .home-page__block--md-3 {
    grid-column: span 3;
  }

  .home-page__block--md-4 {
    grid-column: span 4;
  }
}

@media (min-width: 1024px) {
  .page-content-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .home-page__block--lg-1 {
    grid-column: span 1;
  }

  .home-page__block--lg-2 {
    grid-column: span 2;
  }

  .home-page__block--lg-3 {
    grid-column: span 3;
  }

  .home-page__block--lg-4 {
    grid-column: span 4;
  }

  .home-page__block--lg-5 {
    grid-column: span 5;
  }

  .home-page__block--lg-6 {
    grid-column: span 6;
  }
}

.page-content-grid .home-page__block--rich_text + .home-page__block--rich_text {
  margin-top: 0.5rem;
}

@media (min-width: 640px) {
  .page-content-grid .home-page__block--rich_text + .home-page__block--rich_text {
    margin-top: 1rem;
  }
}

.page-content-grid :deep(.check-list-block),
.page-content-grid :deep(.process-block) {
  margin-bottom: 0;
}

@media (min-width: 768px) {
  .home-page__block--feature_card {
    display: flex;
  }
}

.home-page__block--page_link_card {
  display: flex;
  flex-direction: column;
}

.page-content-grid .home-page__block--page_link_card :deep(.page-link-card) {
  flex: 1;
  margin-bottom: 0;
}
</style>
